import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { initialsFor } from '../lib/types';

/**
 * The avatar in the header, and the menu behind it.
 *
 * A real menu rather than a `<details>`: it has to close on Escape and on a click anywhere
 * else, and the items are a mix of links and an action, which `<summary>` handles poorly.
 */
export default function UserMenu() {
  const { user, profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    // `pointerdown`, not `click`: a click listener fires after the menu item's own handler
    // has already navigated, which closes the menu a frame late and flickers.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="usermenu" ref={wrap}>
      <button
        type="button"
        className="avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title={user?.email ?? undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {initialsFor(profile, user?.displayName, user?.email)}
      </button>

      {open && (
        <div className="menu" role="menu">
          <Link role="menuitem" to="/profile" onClick={() => setOpen(false)}>
            Profile
          </Link>
          <Link role="menuitem" to="/account" onClick={() => setOpen(false)}>
            Account
          </Link>
          <hr />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              // Back to the list first: signing out from /profile would otherwise leave the
              // router on a route the gate no longer renders.
              navigate('/');
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
